import { useForm } from "react-hook-form"
import { useAppSelector } from "../../stateManagement/hooks/redux"
import { useEffect, useState } from "react"
import { configureDatabaseSelectOptions, getAddressTypes, getIdsOfPathsWithGeometry, getObjectTypes, getPaths, getRoadRaionSegments, getRoads } from "./loginFormFunctions"
import { useLoginMutation } from "../../api/rtk/auth/slices/authApiSlice"
import { getItemLocalStorage, setItemLocalStorage, setItemSessionStorage, setItemWebStorage } from "../../features/webStorageSaver"
import { useDispatch } from "react-redux"
import { setAppAuthToken } from "../../features/auth"
import { setLoginFormIsLoading, showToastMessage } from "../../stateManagement/reducers"
import { useLazyFetchIdsOfPathsWithGeometryQuery, useLazyFetchPathsQuery, useLazyFetchRoadsQuery } from "../../api/rtk/base/slices/pathsApiSlice"
import { useLazyFetchAddressTypesQuery, useLazyFetchObjectTypesStructureQuery } from "../../api/rtk/base/slices/objectsApiSlice"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useLazyFetchTableValueQuery } from "../../api/rtk/base/slices/tablesApiSlice"
import SmallLoader from "../loaders/SmallLoader"

export default function LoginForm() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { databasePrefix: databaseUrlPrefix } = useParams()
    const databaseOptions = useAppSelector(state => state.login.databaseOptions)
    const { fullConfig, fullConfigIsLoading } = useAppSelector(state => state.config) ?? {}
    const [formErrorText, setFormErrorText] = useState<any>(null)
    const [signUpButtonIsShown, setSignUpButtonIsShown] = useState<any>(false)
    const [sendLoginCredentials] = useLoginMutation()
    const [fetchPaths] = useLazyFetchPathsQuery()
    const [fetchRoads] = useLazyFetchRoadsQuery()
    const [fetchTableValue] = useLazyFetchTableValueQuery();
    const [fetchIdsOfPathsWithGeometry] = useLazyFetchIdsOfPathsWithGeometryQuery()
    const [fetchObjectTypesStructure] = useLazyFetchObjectTypesStructureQuery()
    const [fetchAddressTypes] = useLazyFetchAddressTypesQuery()
    const lastEnteredDatabase = getItemLocalStorage("lastEnteredDatabase")

    const { register, handleSubmit, watch, getValues, setValue,
        formState: {
            errors,
            isSubmitting
        }
    }: any = useForm({
        defaultValues: {
            database: lastEnteredDatabase ?? "",
            username: "",
            password: "",
            persist: false
        }
    })

    const database = watch("database")

    useEffect(() => {
        if (database && fullConfig) {
            const databaseConfig = fullConfig?.[database]
            const allowRegistration = databaseConfig?.allowRegistration
            setSignUpButtonIsShown(allowRegistration)
        }
    }, [database, fullConfig])

    useEffect(() => {
        if (databaseOptions.length === 1) {
            const databasePrefix = databaseOptions[0]?.prefix
            setValue("database", databasePrefix)
        }
    }, [databaseOptions])


    async function submitForm({ database, username, password, persist }: any) {
        await login({ database, username, password, persist })
    }

    async function login({ database, username, password, persist }: any) {
        dispatch(setLoginFormIsLoading(true))
        const databaseConfig = fullConfig?.[database]
        const databaseUrl = databaseConfig?.irosRestApiUrl
        const geocoderConfig = fullConfig?.geocoder
        const geocoderUrl = geocoderConfig?.irosRestApiUrl
        const credentials = {
            Username: username,
            Password: password
        }
        const result: any = await sendLoginCredentials({ url: databaseUrl, credentials })
        const { data, error } = result ?? {}
        if (data) {
            const appAuthToken = data?.value;
            dispatch(setAppAuthToken(appAuthToken))
            setItemLocalStorage("persist", persist)
            setItemLocalStorage("lastEnteredDatabase", database)
            setItemWebStorage('authTokens', { app: appAuthToken })
            setItemWebStorage('currentConfig', databaseConfig)
            setItemWebStorage('currentApiUrl', databaseUrl)
            setItemWebStorage('geocoderApiUrl', geocoderUrl)
            await getInitData(appAuthToken)
            navigate('/app')
        } else if (error) {
            const status = error?.status
            const code = error?.data?.error?.code
            handleLoginError(status, code)
        }
        dispatch(setLoginFormIsLoading(false))
    }

    function handleLoginError(status: any, code: any) {
        let formErrorText = ""
        let toastErrorText = ""
        if (code === 'Forbidden') {
            formErrorText = 'Неправильный логин или пароль'
            toastErrorText = 'Неправильный логин или пароль'
        }
        if (status === 500) {
            toastErrorText = 'Ошибка сервера'
        }
        if (status !== 500 && code !== 'Forbidden') {
            toastErrorText = 'Не удалось подключиться'
        }
        setFormErrorText(formErrorText);
        dispatch(showToastMessage({ messageText: toastErrorText, messageType: "error" }))
    }

    useEffect(() => {
        if (fullConfig) {
            configureDatabaseSelectOptions(databaseUrlPrefix)
        }
    }, [fullConfig, databaseUrlPrefix])

    async function getInitData(token: any) {
        await getRoads(fetchRoads)
        await getPaths(fetchPaths)
        await getRoadRaionSegments(() => fetchTableValue({ tableValueUrl: "objects/RoadRaionSegments" }))
        await getIdsOfPathsWithGeometry(fetchIdsOfPathsWithGeometry)
        await getObjectTypes(fetchObjectTypesStructure, token)
        await getAddressTypes(fetchAddressTypes)
    }

    function navigateToSignUpPage() {
        setItemSessionStorage('registerDataBase', getValues('database'))
    }

    function navigateToRemindPage() {
        setItemSessionStorage("remindPasswordBase", getValues('database'))
    }

    return (
        <>
            <form className="login-form" onSubmit={handleSubmit(submitForm)}>
                <div className="select-div">
                    <label htmlFor="database-select">База данных</label>
                    {fullConfigIsLoading
                        ?
                        <div className="single-login-database">
                            <SmallLoader iconColor="gray" text="Загрузка" />
                        </div>
                        :
                        <>
                            {databaseOptions.length > 1
                                ?
                                <select
                                    id="database-select"
                                    {...register("database", { required: "Выберите базу данных." })}
                                    aria-invalid={errors["database"]}
                                >
                                    <option disabled value="">Выберите базу данных...</option>
                                    {databaseOptions.map(({ prefix, name }: any) =>
                                        <option selected={prefix === lastEnteredDatabase} value={prefix}>{name}</option>
                                    )}
                                </select>
                                :
                                <div className="single-login-database">{databaseOptions[0]?.name}</div>
                            }
                            {errors["database"] && <span className="form-error" role="alert">{errors["database"]?.message}</span>}
                        </>
                    }
                </div>
                <div className="input-div">
                    <label htmlFor="username-input">Логин</label>
                    <input
                        id="username-input"
                        {...register("username", { required: "Введите логин." })}
                        placeholder="Введите логин..."
                        type="text"
                        aria-required
                        aria-invalid={errors["username"]}
                    />
                    {errors["username"] && <span className="form-error" role="alert">{errors["username"]?.message}</span>}
                </div>
                <div className="input-div">
                    <label htmlFor="password-input">Пароль</label>
                    <input
                        id="password-input"
                        {...register("password", { required: "Введите пароль." })}
                        placeholder="Введите пароль..."
                        type="password"
                        aria-invalid={errors["password"]}
                    />
                    {errors["password"] && <span className="form-error" role="alert">{errors["password"]?.message}</span>}
                </div>
                {formErrorText && <span className="form-error">{formErrorText}</span>}
                <div className="checkbox-div">
                    <input
                        id="persist-checkbox"
                        type="checkbox"
                        {...register("persist")}
                    />
                    <label htmlFor="persist-checkbox">Запомнить</label>
                </div>
                <button disabled={isSubmitting} type="submit" className="login-button">Вход</button>
            </form>
            <div className="control-buttons-div">
                {signUpButtonIsShown &&
                    <div onClick={navigateToSignUpPage}>
                        <Link to="/register">Регистрация</Link>
                    </div>
                }
                <div onClick={navigateToRemindPage}>
                    <Link to="/remind">Забыли пароль?</Link>
                </div>
            </div>
        </>
    )
}
